import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, inject, Input, input } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonAppearance, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { openUrl, resolveValue } from '@lvflanders/bar';
import { resolveValue$ } from '@lvflanders/bar-ng';

import { BarActionButtonOptions } from './bar-action-button-options.model';
import { BarActionButtonType } from './bar-action-button-type.enum';

@Component({
    selector: 'bar-action-button',
    templateUrl: './bar-action-button.html',
    host: { class: 'bar-action-button' },
    imports: [NgClass, NgTemplateOutlet, AsyncPipe, MatBadgeModule, MatTooltipModule, MatButtonModule, MatIconModule]
})
/** @deprecated Use BaasActionButton within &lt;baas-layout&gt; with OnPush change detection. */
export class BarActionButton {
    readonly #router = inject(Router);

    // TODO [STEPET] make this required with angular 22
    @Input() item: BarActionButtonOptions;
    // NOTE needed for ag grid cells with action buttons to avoid losing the cell borders
    /** Whether to inject a zero whiteline if hidden. */
    readonly zeroWhiteLineIfHidden = input(false);

    get appearance(): MatButtonAppearance {
        const normalizedType = this.#normalizeType(this.item?.type);
        return this.#typeToMatAppearance(normalizedType);
    }

    get showIconOnly() {
        return this.item.icon && resolveValue(this.item.icon.iconOnly);
    }
    get routerLink() {
        return resolveValue(this.item.routerLink);
    }

    get disabled() {
        return resolveValue(this.item.disabled);
    }

    get tooltip() {
        return resolveValue$(this.item.title || '');
    }

    get content() {
        return resolveValue$(this.item.content);
    }

    get srText() {
        return resolveValue$(this.item.icon.srText);
    }

    get iconClass() {
        return resolveValue(this.item.icon.class);
    }

    get iconStyle() {
        return resolveValue(this.item.icon.style);
    }

    get badge() {
        return resolveValue$(this.item.counter || null);
    }

    get hidden() {
        return resolveValue(this.item.hidden);
    }

    get type() {
        return !this.item.type ? BarActionButtonType.Default : this.item.type;
    }

    buttonClicked(event) {
        if (this.item.action) this.item.action(event);

        if (this.item.routerLink) {
            this.#router.navigate(resolveValue(this.item.routerLink));
        }

        if (this.item.routerUrl) this.#router.navigateByUrl(resolveValue(this.item.routerUrl));

        if (this.item.href) {
            const url = resolveValue(this.item.href);
            openUrl(url, this.item.target);
        }
    }

    /** Maps deprecated types to their current equivalents. */
    #normalizeType(type?: BarActionButtonType): BarActionButtonType {
        switch (type) {
            case BarActionButtonType.Flat:
            case BarActionButtonType.Raised:
                return BarActionButtonType.Filled;
            case BarActionButtonType.Stroked:
                return BarActionButtonType.Outlined;
            case BarActionButtonType.Default:
            case undefined:
                return BarActionButtonType.Outlined;
            default:
                return type;
        }
    }

    /** Converts type to Material button appearance. */
    #typeToMatAppearance(type: BarActionButtonType): MatButtonAppearance {
        switch (type) {
            case BarActionButtonType.Filled:
                return 'filled';
            case BarActionButtonType.Outlined:
                return 'outlined';
            case BarActionButtonType.Tonal:
                return 'tonal';
            case BarActionButtonType.Text:
                return 'text';
            default:
                // Fallback to outlined for any unexpected values
                return 'outlined';
        }
    }
}
